/**
 * SpotlightOverlay — Full-page spotlight overlay with animated cutout
 *
 * Renders a semi-transparent overlay that highlights UI elements referenced
 * by the assistant. Uses SVG mask for clean cutout rendering with blue glow.
 *
 * Features:
 * - SVG mask technique for pixel-perfect cutout
 * - Framer Motion AnimatePresence for enter/exit transitions
 * - 8px padding around target element
 * - Blue glow effect (pulsing, respects prefers-reduced-motion)
 * - Multi-step guidance with smooth cutout morphing between targets
 * - Next/Previous controls for multi-step navigation
 * - Click overlay or highlighted element to dismiss
 * - z-[70] to sit above chat panel (z-[60]) — spotlight must be topmost when active
 *
 * @see SpotlightProvider for state management
 * @see useElementPosition for target element tracking
 */

import { motion, AnimatePresence } from "framer-motion";
import { useSpotlight } from "@/providers/SpotlightProvider";
import { useElementPosition } from "@/hooks/use-element-position";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

// ---------------------------------------------------------------------------
// Reduced-motion detection
// ---------------------------------------------------------------------------

function usePrefersReducedMotion(): boolean {
  const [prefersReduced, setPrefersReduced] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e: MediaQueryListEvent) => { setPrefersReduced(e.matches); };
    mq.addEventListener("change", handler);
    return () => { mq.removeEventListener("change", handler); };
  }, []);

  return prefersReduced;
}

// ---------------------------------------------------------------------------
// Types & Constants
// ---------------------------------------------------------------------------

interface CutoutRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Padding around the target element for the cutout (px) */
const CUTOUT_PADDING = 8;

/** Border radius for the cutout (px) */
const CUTOUT_RADIUS = 8;

// ---------------------------------------------------------------------------
// SpotlightOverlay
// ---------------------------------------------------------------------------

export function SpotlightOverlay() {
  const {
    isActive,
    activeTargets,
    currentStepIndex,
    totalSteps,
    currentStepDescription,
    nextStep,
    previousStep,
    dismiss,
  } = useSpotlight();
  const reducedMotion = usePrefersReducedMotion();

  // Current target help ID
  const currentTarget =
    isActive && activeTargets.length > 0
      ? activeTargets[currentStepIndex]
      : undefined;

  // Track position of the current target element
  const rect = useElementPosition(currentTarget ?? null);

  // Click handler for the cutout area — dismisses spotlight
  const handleCutoutClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      dismiss();
    },
    [dismiss],
  );

  // Keyboard navigation for multi-step
  useEffect(() => {
    if (!isActive) return;

    const onKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "Escape":
          dismiss();
          break;
        case "ArrowRight":
        case "Enter":
          if (totalSteps > 1) nextStep();
          else dismiss();
          break;
        case "ArrowLeft":
          if (totalSteps > 1) previousStep();
          break;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => { window.removeEventListener("keydown", onKeyDown); };
  }, [isActive, totalSteps, nextStep, previousStep, dismiss]);

  // Don't render anything when inactive
  if (!isActive) return null;

  // Compute cutout rect with padding
  const cutout: CutoutRect | null = rect
    ? {
        x: rect.x - CUTOUT_PADDING,
        y: rect.y - CUTOUT_PADDING,
        width: rect.width + CUTOUT_PADDING * 2,
        height: rect.height + CUTOUT_PADDING * 2,
      }
    : null;

  const overlayDuration = reducedMotion ? 0 : 0.25;
  const isMultiStep = totalSteps > 1;

  return (
    <AnimatePresence>
        <motion.div
          key="spotlight-overlay"
          className="fixed inset-0 z-[70]"
          initial={{ opacity: reducedMotion ? 1 : 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: overlayDuration }}
          // Click overlay background to dismiss
          onClick={dismiss}
          style={{ pointerEvents: "auto" }}
        >
          {/* SVG mask overlay */}
          <svg
            className="absolute inset-0 w-full h-full"
            style={{ pointerEvents: "none" }}
          >
            <defs>
              <mask id="spotlight-mask">
                {/* White = visible (shows the dark overlay) */}
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                {/* Black = transparent (creates the cutout) */}
                {cutout && (
                  <motion.rect
                    key="cutout-morph"
                    fill="black"
                    rx={CUTOUT_RADIUS}
                    ry={CUTOUT_RADIUS}
                    initial={{
                      x: cutout.x,
                      y: cutout.y,
                      width: cutout.width,
                      height: cutout.height,
                      opacity: reducedMotion ? 1 : 0,
                    }}
                    animate={{
                      x: cutout.x,
                      y: cutout.y,
                      width: cutout.width,
                      height: cutout.height,
                      opacity: 1,
                    }}
                    transition={
                      reducedMotion
                        ? { duration: 0 }
                        : {
                            duration: 0.35,
                            ease: [0.25, 0.1, 0.25, 1],
                          }
                    }
                  />
                )}
              </mask>
            </defs>

            {/* Semi-transparent dark overlay with mask cutout */}
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="rgba(0, 0, 0, 0.5)"
              mask="url(#spotlight-mask)"
              style={{ pointerEvents: "auto", cursor: "pointer" }}
            />
          </svg>

          {/* Clickable cutout area — click target element to dismiss */}
          {cutout && (
            <div
              role="presentation"
              tabIndex={-1}
              onClick={handleCutoutClick}
              style={{
                position: "absolute",
                left: cutout.x,
                top: cutout.y,
                width: cutout.width,
                height: cutout.height,
                borderRadius: CUTOUT_RADIUS,
                cursor: "pointer",
                pointerEvents: "auto",
              }}
            />
          )}

          {/* Blue glow effect around cutout */}
          {cutout && (
            <GlowEffect
              cutout={cutout}
              reducedMotion={reducedMotion}
              stepIndex={currentStepIndex}
            />
          )}

          {/* Step controls for multi-step mode — or description tooltip for single-step */}
          {cutout && (isMultiStep || currentStepDescription) && (
            <StepControls
              currentStep={currentStepIndex + 1}
              totalSteps={totalSteps}
              description={currentStepDescription}
              cutout={cutout}
              reducedMotion={reducedMotion}
              onNext={nextStep}
              onPrevious={previousStep}
              onDismiss={dismiss}
            />
          )}
        </motion.div>
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// GlowEffect — animated blue glow around the highlighted element
// ---------------------------------------------------------------------------

interface GlowEffectProps {
  cutout: CutoutRect;
  reducedMotion: boolean;
  stepIndex: number;
}

function GlowEffect({ cutout, reducedMotion, stepIndex }: GlowEffectProps) {
  if (reducedMotion) {
    return (
      <div
        style={{
          position: "absolute",
          left: cutout.x,
          top: cutout.y,
          width: cutout.width,
          height: cutout.height,
          borderRadius: CUTOUT_RADIUS,
          pointerEvents: "none",
          boxShadow: "0 0 12px 2px rgba(255, 255, 255, 0.15)",
        }}
      />
    );
  }

  return (
    <motion.div
      key={`glow-step-${String(stepIndex)}`}
      style={{
        position: "absolute",
        borderRadius: CUTOUT_RADIUS,
        pointerEvents: "none",
      }}
      initial={{
        left: cutout.x,
        top: cutout.y,
        width: cutout.width,
        height: cutout.height,
        boxShadow: "0 0 12px 2px rgba(255, 255, 255, 0.15)",
      }}
      animate={{
        left: cutout.x,
        top: cutout.y,
        width: cutout.width,
        height: cutout.height,
        boxShadow: [
          "0 0 12px 2px rgba(255, 255, 255, 0.15)",
          "0 0 18px 4px rgba(255, 255, 255, 0.25)",
          "0 0 12px 2px rgba(255, 255, 255, 0.15)",
        ],
      }}
      transition={{
        left: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] },
        top: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] },
        width: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] },
        height: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] },
        boxShadow: { duration: 2.5, repeat: Infinity, ease: "easeInOut" },
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// StepControls — Multi-step navigation with step indicator
// ---------------------------------------------------------------------------

interface StepControlsProps {
  currentStep: number;
  totalSteps: number;
  description?: string | undefined;
  cutout: CutoutRect;
  reducedMotion: boolean;
  onNext: () => void;
  onPrevious: () => void;
  onDismiss: () => void;
}

function StepControls({
  currentStep,
  totalSteps,
  description,
  cutout,
  reducedMotion,
  onNext,
  onPrevious,
  onDismiss,
}: StepControlsProps) {
  const isFirst = currentStep === 1;
  const isLast = currentStep === totalSteps;
  const isMultiStep = totalSteps > 1;

  // Viewport-aware positioning — compute actual left-edge (no CSS transform)
  const TOOLTIP_GAP = 12;
  const TOOLTIP_HEIGHT_ESTIMATE = description ? 80 : 40;
  const TOOLTIP_WIDTH = 280;
  const CHAT_PANEL_WIDTH = 400;
  const EDGE_MARGIN = 12;

  const viewportW = typeof window !== "undefined" ? window.innerWidth : 1400;
  const viewportH = typeof window !== "undefined" ? window.innerHeight : 900;

  // Vertical: prefer below, flip above if it would overflow
  const belowY = cutout.y + cutout.height + TOOLTIP_GAP;
  const aboveY = cutout.y - TOOLTIP_GAP - TOOLTIP_HEIGHT_ESTIMATE;
  const placeAbove = belowY + TOOLTIP_HEIGHT_ESTIMATE > viewportH - 60;
  const top = placeAbove ? Math.max(EDGE_MARGIN, aboveY) : belowY;

  // Horizontal: center on cutout, then clamp left-edge directly (no translateX)
  const cutoutCenterX = cutout.x + cutout.width / 2;
  const idealLeft = cutoutCenterX - TOOLTIP_WIDTH / 2;
  const maxLeft = viewportW - CHAT_PANEL_WIDTH - TOOLTIP_WIDTH - EDGE_MARGIN;
  const left = Math.max(EDGE_MARGIN, Math.min(idealLeft, maxLeft));

  return (
    <motion.div
      className="absolute flex flex-col items-center gap-1.5 rounded-xl bg-zinc-900/95 border border-white/[0.15] shadow-xl backdrop-blur-md"
      style={{
        top,
        left,
        pointerEvents: "auto",
        padding: description ? "8px 12px" : "4px 6px",
        width: TOOLTIP_WIDTH,
      }}
      initial={{
        opacity: reducedMotion ? 1 : 0,
        y: reducedMotion ? 0 : (placeAbove ? 8 : -8),
      }}
      animate={{ opacity: 1, y: 0 }}
      transition={
        reducedMotion ? { duration: 0 } : { duration: 0.2, delay: 0.15 }
      }
      onClick={(e) => { e.stopPropagation(); }}
    >
      {/* Step description text */}
      {description && (
        <p className="text-xs text-white/90 leading-relaxed text-center">
          {description}
        </p>
      )}

      {/* Navigation controls */}
      <div className="flex items-center gap-1">
        {isMultiStep && (
          <button
            onClick={onPrevious}
            disabled={isFirst}
            className="flex items-center justify-center h-6 w-6 rounded text-white/80 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Previous step"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
        )}

        {/* Step indicator */}
        {isMultiStep && (
          <span className="px-2 text-xs font-medium text-white/90 tabular-nums select-none whitespace-nowrap">
            {currentStep} / {totalSteps}
          </span>
        )}

        {/* Next/Done button */}
        {isMultiStep ? (
          <button
            onClick={onNext}
            className="flex items-center justify-center h-6 w-6 rounded text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            aria-label={isLast ? "Finish" : "Next step"}
          >
            {isLast ? (
              <X className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ) : (
          <button
            onClick={onDismiss}
            className="flex items-center justify-center h-6 px-2 rounded text-xs text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Dismiss"
          >
            Got it
          </button>
        )}
      </div>
    </motion.div>
  );
}
